import { FC, ReactNode } from 'react';
import { Button, ButtonProps, Popover, PopoverTrigger, PopoverContent, PopoverBody, Placement, useDisclosure, Text } from '@chakra-ui/react';
import { ICON_BY_NAME } from 'ui/components/Icons/Icon';
import { upFirst } from 'ui/helpers';
import { CbOptionSelectItem } from './CbOptionSelectItem';
type Option = string | {};
export interface CbOptionsButtonProps extends Partial<Omit<ButtonProps, 'defaultValue'>> {
  contentProps?: any;
  contentBackgroundColor?: string;
  defaultValue?: Option;
  dividerColor?: string;
  options: Option[];
  optionNameKey?: string;
  optionId?: number | string;
  placeholder?: string;
  onChange?: (option: Option) => void;
  renderItemOverride?: (option: Option, setOption: (option: Option) => void) => void;
  renderValueOverride?: (option: Option) => void;
  placement?: Placement;
  textColor?: string;
}
export const CbOptionsButton: FC<CbOptionsButtonProps> = ({
  contentBackgroundColor = 'white',
  contentProps = {},
  defaultValue,
  dividerColor,
  options,
  optionNameKey,
  optionId,
  placeholder = 'Select',
  onChange,
  placement,
  renderItemOverride,
  renderValueOverride,
  textColor = 'text.primary',
  ...props
}) => {
  const {
    isOpen,
    onOpen,
    onClose
  } = useDisclosure();
  const renderValue = option => <Text marginRight={1} paddingTop="2px" textAlign="left" data-sentry-element="Text" data-sentry-component="renderValue" data-sentry-source-file="CbOptionsButton.tsx">
      {optionNameKey ? upFirst(option[optionNameKey]) : upFirst(option)}
    </Text>;
  const renderItem = (option, setOption) => {
    const lastItem = options?.[options.length - 1];
    return <CbOptionSelectItem key={optionId ? option[optionId] : option} borderColor={option === lastItem ? 'transparent' : dividerColor} color={textColor} onClick={() => setOption(option)} data-sentry-element="CbOptionSelectItem" data-sentry-component="renderItem" data-sentry-source-file="CbOptionsButton.tsx">
        {renderValue(option)}
      </CbOptionSelectItem>;
  };
  const setOption = (option: Option) => {
    if (onChange) {
      onChange(option);
    }
    onClose();
  };
  const renderItems = renderItemOverride ? options.map(option => renderItemOverride(option, setOption)) : options.map(option => renderItem(option, setOption));
  return <Popover isOpen={isOpen} onClose={onClose} placement={placement || 'bottom-start'} autoFocus={true} data-sentry-element="Popover" data-sentry-component="CbOptionsButton" data-sentry-source-file="CbOptionsButton.tsx">
      <PopoverTrigger data-sentry-element="PopoverTrigger" data-sentry-source-file="CbOptionsButton.tsx">
        <Button border="1px solid" borderColor="cloud.300" borderRadius={99} fontWeight="semibold" justifyContent="space-between" color="text.primary" colorScheme="transparent" onClick={onOpen} {...props} data-sentry-element="Button" data-sentry-source-file="CbOptionsButton.tsx">
          <ICON_BY_NAME.dotshorizontal width="100%" fontSize={14} data-sentry-element="unknown" data-sentry-source-file="CbOptionsButton.tsx" />
        </Button>
      </PopoverTrigger>
      <PopoverContent background={contentBackgroundColor} borderRadius={8} border="none" boxShadow="0px 3px 15px 2px rgba(0, 0, 0, 0.09)" _focus={{
      boxShadow: '0px 3px 15px 2px rgba(0, 0, 0, 0.09) !important',
      outline: 'none'
    }} {...contentProps} data-sentry-element="PopoverContent" data-sentry-source-file="CbOptionsButton.tsx">
        <PopoverBody padding={0} data-sentry-element="PopoverBody" data-sentry-source-file="CbOptionsButton.tsx">{renderItems as ReactNode}</PopoverBody>
      </PopoverContent>
    </Popover>;
};